/**
 * The L2 response cache — read its state, and flush it with a reason (arch-14 §6).
 *
 * **Everything rendered is what Redis answered.** The entry count is the keys written under the
 * current version, not an estimate, and the families are the server's list. A 503 means Redis did
 * not answer — said as such, never as an empty cache, because "empty" and "unreachable" send an
 * operator to different places.
 *
 * **A flush is the last resort, not the fix.** Writes already bump the version for what they
 * touch; a flush is for the case where something was missed, and it sends every public read to
 * MySQL until the cache refills. Hence superadmin only, a typed phrase and an audited reason.
 *
 * No design shows this; built from 02-architecture/14 §6 per the 2026-10-26 ruling.
 */
import { useState } from 'react';
import { KV } from '../components/entities';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useFlushResponseCache, useResponseCacheState } from '../lib/api/hooks/responseCache';

/** "300 s" reads as a number to work out; "5 min" reads as an answer. */
export function ttlLabel(seconds: number): string {
  if (seconds >= 3600 && seconds % 3600 === 0) return `${seconds / 3600} h`;
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60} min`;
  return `${seconds} s`;
}

export function ResponseCache() {
  const { scope, toast } = useConsole();
  const state = useResponseCacheState();
  const flush = useFlushResponseCache();
  const [confirm, setConfirm] = useState(false);

  return (
    <Section title="Response cache" note="What the public read cache holds now, read from Redis">
      {state.isPending && <p>Reading the cache…</p>}
      {state.isError && <p role="alert">Could not read the cache — {errorMessage(state.error)}</p>}
      {state.isSuccess && (
        <KV
          rows={[
            ['Version', String(state.data.version)],
            ['Entries', state.data.entries.toLocaleString('en-IN')],
            ['Lifetime', ttlLabel(state.data.ttl_seconds)],
            ['Families', state.data.families.length > 0 ? state.data.families.join(', ') : 'None cached'],
          ]}
        />
      )}
      <div style={{ marginTop: 'var(--sp-md)' }}>
        <ScopeAction scope={scope} need="superadmin" size="sm" onClick={() => setConfirm(true)}>
          Flush the cache
        </ScopeAction>
      </div>

      <ConfirmAction
        open={confirm}
        title="Flush the response cache?"
        phrase="FLUSH"
        tone="error"
        reason="The incident this flush answers (required, audited)"
        consequences={[
          'Bumps the version, so every cached public read is fetched afresh from MySQL.',
          'Expect slower pages and more database load until the cache refills.',
          'Writes already clear what they touch — flush only when something was missed.',
        ]}
        onCancel={() => setConfirm(false)}
        onConfirm={(reason) =>
          flush.mutate(reason, {
            onSuccess: (out) => {
              toast({ tone: 'success', message: `Cache flushed · version ${out.previous_version} → ${out.version}` });
              setConfirm(false);
            },
            onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
          })
        }
        confirmLabel="Flush"
      />
    </Section>
  );
}
